<template>
  <div
    class="breadcrumb-item"
    :class="{
      hover: isHovered,
      focused: isFocused,
      activated: isActivated,
      disabled,
      current: isCurrent,
      skeleton,
    }"
    @mouseover="isHovered = true"
    @mouseleave="isHovered = false"
    @focus="isFocused = true"
    @blur="isFocused = false"
    @click="handleClick"
  >
    <div class="label-container">
      <ToolTip v-if="shouldTruncate" :content="label" position="bottom">
        <span class="label">{{ truncatedLabel }}</span>
      </ToolTip>
      <span v-else class="label">{{ truncatedLabel }}</span>
    </div>
    <span v-if="!isCurrent" class="separator">/</span>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import ToolTip from '../ToolTip/ToolTip.vue';

export default defineComponent({
  name: 'Breadcrumb',
  components: {
    ToolTip,
  },
  props: {
    label: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isCurrent: {
      type: Boolean,
      default: false,
    },
    maxLength: {
      type: Number,
      default: 10,
    },
    skeleton: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const isHovered = ref(false);
    const isFocused = ref(false);
    const isActivated = ref(false);
    const truncatedLabel = computed(() => {
      if (props.label.length > props.maxLength) {
        return `${props.label.substring(0, props.maxLength)}...`;
      }
      return props.label;
    });

    const shouldTruncate = computed(() => props.label.length > props.maxLength);

    const handleClick = () => {
      if (!props.disabled) {
        isActivated.value = true;
        setTimeout(() => {
          isActivated.value = false;
        }, 200);
      }
    };

    return {
      isHovered,
      isFocused,
      isActivated,
      truncatedLabel,
      shouldTruncate,
      handleClick,
    };
  },
});
</script>

<style scoped>
.breadcrumb-item {
  display: flex;
  align-items: center;
  height: 24px;
  gap: 6px;
  cursor: pointer;
}
.label-container {
  gap: 10px;
  padding: 0 1px;
}

.label {
  font-family: 'Jost', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0;
  color: #492b7c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.separator {
  width: 11px;
  height: 24px;
  font-family: 'Jost', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  letter-spacing: 0;
  vertical-align: middle;
}

.breadcrumb-item:hover .label {
  color: #674e92;
}

.breadcrumb-item.focused .label {
  color: #8672a8;
}

.breadcrumb-item.activated .label {
  color: #000000;
}

.breadcrumb-item.disabled {
  cursor: not-allowed;
}

.breadcrumb-item.disabled .label {
  color: #bdbdbd;
}

.breadcrumb-item.current .label {
  color: #000000;
}

.breadcrumb-item.skeleton .label {
  background-color: #f5f5f5;
  color: transparent;
  border-radius: 4px;
  min-width: 60px;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 1;
  }
}
</style>
